import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import Modal from './Modal';
import { formatUsdAmount } from '../utils/money';

function ClaimRow({ label, data }) {
  if (!data) return null;
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 bg-gray-50/80 dark:bg-gray-900/40">
      <div className="text-sm font-semibold text-gray-900 dark:text-white mb-2">{label}</div>
      <div className="grid grid-cols-3 gap-2 text-xs">
        <div>
          <p className="text-gray-500 dark:text-gray-400">Total</p>
          <p className="font-bold tabular-nums text-gray-900 dark:text-white">{formatUsdAmount(data.total)}</p>
        </div>
        <div>
          <p className="text-emerald-600 dark:text-emerald-400">Claimed</p>
          <p className="font-bold tabular-nums text-emerald-700 dark:text-emerald-300">
            {formatUsdAmount(data.claimed)}
            <span className="block text-[10px] font-normal text-gray-500 dark:text-gray-400">
              {data.claimedCount ?? 0} payout(s)
            </span>
          </p>
        </div>
        <div>
          <p className="text-amber-600 dark:text-amber-400">Unclaimed</p>
          <p className="font-bold tabular-nums text-amber-700 dark:text-amber-300">
            {formatUsdAmount(data.unclaimed)}
            <span className="block text-[10px] font-normal text-gray-500 dark:text-gray-400">
              {data.unclaimedCount ?? 0} payout(s)
            </span>
          </p>
        </div>
      </div>
    </div>
  );
}

export default function EventClaimStatsModal({ kind, item, onClose, apiBase = 'admin' }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const base = kind === 'match' ? 'matches' : 'polls';
  const titleLabel =
    item?.label ||
    (kind === 'match' ? `${item?.teamA || ''} vs ${item?.teamB || ''}` : item?.question || 'Event');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const { data } = await api.get(`/${apiBase}/${base}/${item._id}/claim-stats`);
        if (!cancelled) setStats(data);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.message || e.message || 'Failed to load claim stats');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiBase, base, item._id]);

  const grand = stats?.claims?.grandTotal;

  return (
    <Modal isOpen onClose={onClose} title={`View claims — ${titleLabel}`}>
      {loading ? (
        <div className="py-10 text-center text-gray-500 dark:text-gray-400">Loading claim data…</div>
      ) : error ? (
        <div className="py-6 text-center text-red-600 dark:text-red-400 text-sm">{error}</div>
      ) : (
        <div className="space-y-5 max-h-[70vh] overflow-y-auto pr-1">
          <div className="rounded-xl border-2 border-slate-200 dark:border-slate-600 p-4 bg-white dark:bg-gray-800">
            <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-3">Claims overview</h3>
            {grand ? (
              <div className="grid grid-cols-3 gap-3 mb-4 text-sm">
                <div className="text-center p-2 rounded-lg bg-slate-100 dark:bg-slate-900/50">
                  <p className="text-xs text-gray-500 dark:text-gray-400">Grand total</p>
                  <p className="font-bold tabular-nums text-lg text-gray-900 dark:text-white">
                    {formatUsdAmount(grand.total)}
                  </p>
                </div>
                <div className="text-center p-2 rounded-lg bg-emerald-50 dark:bg-emerald-950/30">
                  <p className="text-xs text-emerald-700 dark:text-emerald-300">Claimed</p>
                  <p className="font-bold tabular-nums text-lg text-emerald-800 dark:text-emerald-200">
                    {formatUsdAmount(grand.claimed)}
                  </p>
                </div>
                <div className="text-center p-2 rounded-lg bg-amber-50 dark:bg-amber-950/30">
                  <p className="text-xs text-amber-700 dark:text-amber-300">Unclaimed</p>
                  <p className="font-bold tabular-nums text-lg text-amber-800 dark:text-amber-200">
                    {formatUsdAmount(grand.unclaimed)}
                  </p>
                </div>
              </div>
            ) : null}
            <div className="space-y-3">
              <ClaimRow label="Free jackpot" data={stats?.claims?.free} />
              <ClaimRow label="Boost" data={stats?.claims?.boost} />
              <ClaimRow label="Market" data={stats?.claims?.market} />
            </div>
          </div>

          <div className="rounded-xl border border-blue-200 dark:border-blue-800 p-4 bg-blue-50/50 dark:bg-blue-950/20">
            <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-1">Market user buy volume</h3>
            <p className="text-[11px] text-gray-500 dark:text-gray-400 mb-3">
              Net USDC users deployed (buys minus sells, fees excluded, market-maker trades excluded).
            </p>
            <div className="flex justify-between items-center mb-3 p-3 rounded-lg bg-white dark:bg-gray-800 border border-blue-100 dark:border-blue-900/50">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Total all options</span>
              <span className="text-lg font-bold tabular-nums text-blue-800 dark:text-blue-200">
                {formatUsdAmount(stats?.marketVolume?.totalNetBuy ?? 0)}
              </span>
            </div>
            <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
              <table className="min-w-full text-xs">
                <thead className="bg-gray-100 dark:bg-gray-900/60 text-gray-600 dark:text-gray-400">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold">Option</th>
                    <th className="px-3 py-2 text-right font-semibold">Orderbook net</th>
                    <th className="px-3 py-2 text-right font-semibold">AMM net</th>
                    <th className="px-3 py-2 text-right font-semibold">Total net buy</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700 bg-white dark:bg-gray-800">
                  {(stats?.marketVolume?.byOption || []).map((row) => (
                    <tr key={row.optionKey}>
                      <td className="px-3 py-2 font-medium text-gray-900 dark:text-white">{row.label}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-gray-700 dark:text-gray-300">
                        {formatUsdAmount(row.orderbookNetBuy)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-gray-700 dark:text-gray-300">
                        {formatUsdAmount(row.ammNetBuy)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums font-semibold text-gray-900 dark:text-white">
                        {formatUsdAmount(row.netBuy)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {stats?.pools ? (
            <div className="text-[11px] text-gray-500 dark:text-gray-400 grid grid-cols-2 gap-2 pt-1 border-t border-gray-200 dark:border-gray-700">
              <span>Free pool: {formatUsdAmount(stats.pools.freeJackpotPool)}</span>
              <span>Boost pool: {formatUsdAmount(stats.pools.boostPool)}</span>
              <span>Platform fees: {formatUsdAmount(stats.pools.platformFees)}</span>
              <span>Market fees: {formatUsdAmount(stats.pools.marketPlatformFees)}</span>
            </div>
          ) : null}
        </div>
      )}
      <div className="mt-4 flex justify-end">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
        >
          Close
        </button>
      </div>
    </Modal>
  );
}
